import React, { useState, useEffect } from 'react';
import { ordersAPI } from '../../utils/api';
import { LoadingSpinner, StatusBadge } from '../../components/common';

const STATUS_OPTIONS = ['placed', 'packed', 'shipped', 'delivered', 'cancelled'];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sortCol, setSortCol] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const load = () => {
    setLoading(true);
    ordersAPI.getAll({ status: statusFilter, limit: 100 })
      .then(r => setOrders(r.data.orders))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const viewOrder = async (order) => {
    setDetailLoading(true);
    const res = await ordersAPI.getById(order.id);
    setSelectedOrder(res.data);
    setDetailLoading(false);
  };

  const updateStatus = async (orderId, status) => {
    await ordersAPI.updateStatus(orderId, status);
    load();
    if (selectedOrder?.id === orderId) {
      setSelectedOrder(prev => ({ ...prev, status }));
    }
  };

  const addr = (order) => {
    try {
      const a = typeof order.shipping_address === 'string' ? JSON.parse(order.shipping_address) : order.shipping_address;
      return a ? `${a.city}, ${a.pincode}` : '—';
    } catch { return '—'; }
  };

  const toggleSort = (col) => { setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc'); setSortCol(col); };
  const si = (col) => <span style={{ fontSize: '0.65rem', marginLeft: 2, opacity: sortCol === col ? 1 : 0.3 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>;
  const sortedOrders = [...orders].sort((a, b) => {
    const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
    const n = parseFloat(va), m = parseFloat(vb);
    const cmp = !isNaN(n) && !isNaN(m) ? n - m : String(va).toLowerCase().localeCompare(String(vb).toLowerCase());
    return sortDir === 'desc' ? -cmp : cmp;
  });

  return (
    <div>
      <div className="page-header">
        <h1>Orders</h1>
        <p>{orders.length} orders{statusFilter ? ` (${statusFilter})` : ''}</p>
      </div>

      {/* Status Filter */}
      <div className="flex gap-2" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          className={`btn btn-sm ${!statusFilter ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setStatusFilter('')}
        >All</button>
        {STATUS_OPTIONS.map(s => (
          <button
            key={s}
            className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setStatusFilter(s)}
            style={{ textTransform: 'capitalize' }}
          >{s}</button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Orders Table */}
        <div style={{ flex: 2, minWidth: 400 }}>
          {loading ? <LoadingSpinner /> : (
            <div className="card">
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('order_number')}>Order # {si('order_number')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('customer_name')}>Customer {si('customer_name')}</th>
                      <th>Address</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('total')}>Total {si('total')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('status')}>Status {si('status')}</th>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('created_at')}>Date {si('created_at')}</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedOrders.map(o => (
                      <tr key={o.id} style={{ cursor: 'pointer' }} onClick={() => viewOrder(o)}>
                        <td style={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85rem' }}>{o.order_number}</td>
                        <td style={{ fontSize: '0.85rem' }}>
                          <div>{o.customer_name || 'Guest'}</div>
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{o.customer_email || o.guest_email}</div>
                        </td>
                        <td style={{ fontSize: '0.82rem' }}>{addr(o)}</td>
                        <td style={{ fontWeight: 700 }}>${parseFloat(o.total).toFixed(2)}</td>
                        <td><StatusBadge status={o.status} /></td>
                        <td style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                          {new Date(o.created_at).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' })}
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <select
                            className="form-select"
                            style={{ fontSize: '0.78rem', padding: '0.2rem 0.4rem' }}
                            value={o.status}
                            onChange={e => updateStatus(o.id, e.target.value)}
                          >
                            {STATUS_OPTIONS.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>No orders found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Order Detail Panel */}
        {selectedOrder && (
          <div style={{ flex: 1, minWidth: 280 }}>
            <div className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ fontWeight: 700, fontSize: '1rem' }}>#{selectedOrder.order_number}</h2>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#6b7280' }} onClick={() => setSelectedOrder(null)}>✕</button>
              </div>

              {detailLoading ? <LoadingSpinner /> : (
                <>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <StatusBadge status={selectedOrder.status} />
                  </div>

                  <div style={{ fontSize: '0.82rem', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.2rem' }}>Customer</div>
                    <div>{selectedOrder.customer_name || 'Guest'}</div>
                  </div>

                  {selectedOrder.shipping_address && (() => {
                    const a = typeof selectedOrder.shipping_address === 'string'
                      ? JSON.parse(selectedOrder.shipping_address)
                      : selectedOrder.shipping_address;
                    return (
                      <div style={{ fontSize: '0.82rem', marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 700, marginBottom: '0.2rem' }}>Ship To</div>
                        <div>{a.full_name}<br />{a.address_line1}<br />{a.city} - {a.pincode}</div>
                      </div>
                    );
                  })()}

                  <div style={{ fontSize: '0.82rem', marginBottom: '1rem' }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.4rem' }}>Items</div>
                    {selectedOrder.items?.map(item => (
                      <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', borderBottom: '1px solid #f3f4f6' }}>
                        <span>{item.name} × {item.qty}</span>
                        <span style={{ fontWeight: 600 }}>${parseFloat(item.total_price).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div style={{ fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Subtotal</span><span>${parseFloat(selectedOrder.subtotal).toFixed(2)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Shipping</span><span>${parseFloat(selectedOrder.shipping_fee).toFixed(2)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.2rem 0' }}><span>Tax</span><span>${parseFloat(selectedOrder.tax).toFixed(2)}</span></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0', fontWeight: 800, borderTop: '2px solid #e5e7eb', marginTop: '0.3rem' }}>
                      <span>Total</span><span>${parseFloat(selectedOrder.total).toFixed(2)}</span>
                    </div>
                  </div>

                  <div style={{ marginTop: '1rem' }}>
                    <label className="form-label">Update Status</label>
                    <select
                      className="form-select w-full"
                      value={selectedOrder.status}
                      onChange={e => updateStatus(selectedOrder.id, e.target.value)}
                    >
                      {STATUS_OPTIONS.map(s => <option key={s} value={s} style={{ textTransform: 'capitalize' }}>{s}</option>)}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOrders;
